import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Folder,
  Image as ImageIcon,
  Info,
  Link as LinkIcon,
  Loader2,
  Plus,
  RefreshCcw,
  Trash2,
  Upload,
} from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import { ConfirmModal, Input, PageLeaveConfirmModal } from '../../../components/ui';
import { useAppContext } from '../../../context/AppContext';
import { getWorkspaceArtifactContent, getWorkspaceArtifactOptions } from '../../../services/api';
import { apiFetch } from '../../../services/apiClient';
import {
  mapPlanContentToTestForm,
} from '../../research/utils/researchArtifacts';
import {
  ErrorBanner,
  PersonaAvatar,
  PersonaState,
  PersonaTagChip,
  TokenQuotaCtaHint,
} from '../components/PersonaShared';
import { useB2bTokenQuota } from '../hooks/useB2bTokenQuota';
import { isQuotaExceededError } from '../../../services/apiClient';
import { getQuotaExceededMessage } from '../../../utils/productAccess';
import { GenerationProgressView } from '../components/GenerationProgressView';
import ResearchPlanImportModal from '../components/ResearchPlanImportModal';
import {
  personaAbTestApi,
  personaApi,
  personaAssetUrl,
  personaFigmaApi,
  personaTestApi,
  resolvePersonaApiErrorMessage,
  sortPersonaFolders,
} from '../services/personaService';
import { getTestCreateCopy } from '../constants/personaDomainCopy';
import '../styles/persona.scss';

const SOURCE_OPTIONS = [
  { value: 'image', label: 'Image 첨부', icon: ImageIcon },
  { value: 'url', label: 'URL 입력', icon: LinkIcon },
  { value: 'figma', label: 'Figma 연동', figma: true },
];

const UI_PERSONA_SELECTION_LIMIT = 6;
const AB_PERSONA_SELECTION_LIMIT = 12;
const SINGLE_SOURCE_ITEMS_LIMIT = 5;
const FLOW_SOURCE_ITEMS_LIMIT = 10;
const AB_FLOW_SOURCE_ITEMS_LIMIT = 5;
const MIN_FLOW_SOURCE_ITEMS = 2;
const MAX_IMAGE_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const TEST_FIGMA_RETURN_PATH_KEY = 'reopsai:test-create-figma-return-path';
const COMMON_TEST_ANALYSIS_STEPS = {
  persona: { key: 'persona', label: '퍼소나 로드', message: '테스트를 수행할 퍼소나의 성향과 경험 데이터를 불러오고 있습니다.' },
};
const TEST_ANALYSIS_STEP_VARIANTS = {
  ui: {
    screen: [
      { key: 'screen-composition', label: '화면 구성 분석', message: '각 퍼소나의 관점에서 화면의 정보 구조와 주요 디자인 요소들을 파악합니다.' },
      { key: 'feedback', label: '피드백 수집', message: '화면에 대해 퍼소나가 느낄 실제 의견과 반응을 수집합니다.' },
    ],
    flow: [
      { key: 'journey', label: '사용자 여정 분석', message: '전체 프로세스 흐름을 따라가며 퍼소나가 느끼는 인지적 부하를 추적합니다.' },
      { key: 'bottleneck', label: '이탈 및 병목 구간 식별', message: '여정 중 허들이 발생하는 핵심 구간과 그 원인을 분석합니다.' },
    ],
  },
  ab: {
    screen: [
      { key: 'variant-comparison', label: '시안 대조 분석', message: '화면에 대해 퍼소나가 느끼는 의견과 반응을 상대적으로 비교합니다.' },
      { key: 'preference', label: '선호도 및 근거 분석', message: '시안별 선호도와 그 선택의 기준이 되는 구체적인 이유를 도출합니다.' },
    ],
    flow: [
      { key: 'journey', label: '사용자 여정 분석', message: '전체 프로세스 흐름을 따라가며 퍼소나가 느끼는 인지적 부하를 상대적으로 비교합니다.' },
      { key: 'preference', label: '선호도 및 근거 분석', message: '시안별 선호도와 그 선택의 기준이 되는 구체적인 이유를 도출합니다.' },
    ],
  },
};
const getTestAnalysisSteps = (testType, scopeType) => {
  const variant = TEST_ANALYSIS_STEP_VARIANTS[testType]?.[scopeType] || TEST_ANALYSIS_STEP_VARIANTS.ui.screen;
  const reportMessage = testType === 'ab'
    ? '시안별 비교 결과를 바탕으로 최종 리포트를 작성 중입니다.'
    : '화면별 주요 의견을 바탕으로 최종 리포트를 작성 중입니다.';
  return [
    COMMON_TEST_ANALYSIS_STEPS.persona,
    ...variant,
    { key: 'report', label: '리포트 작성', message: reportMessage },
  ];
};
const ANALYSIS_STAGE_INTERVAL_MS = 10000;
const TEST_CREATE_COPY = {
  ui: {
    title: '화면 사용성 검증',
    description: '퍼소나의 시각에서 화면 UI의 문제점을 진단하고 사용성을 검증합니다.',
    typeLabel: '화면 사용성 검증',
    namePlaceholder: "테스트 이름을 입력해주세요. (예:멤버십 메인 하단 '주변 혜택' 아이콘의 직관성 및 메뉴 진입 편의성 확인)",
    descriptionPlaceholder: "테스트 내용을 입력해주세요. (예:신규 멤버십 바코드 노출 화면에서 유저가 제휴사 혜택을 탐색할 때, 핵심 기능인 '바코드 제시'가 최우선으로 인지되는지 검증하고자 함)",
  },
  ab: {
    title: 'A/B 비교검증',
    description: '시안의 A/B 비교검증을 통해 퍼소나가 선호하는 최적의 안을 제안합니다.',
    typeLabel: 'A/B 비교검증',
    namePlaceholder: '테스트 이름을 입력해주세요. (예: 요금제 변경 시, 혜택 정보 안내 태그 디자인 및 위치의 UI 선호 비교)',
    descriptionPlaceholder: "테스트내용을 입력해주세요. (예:현재 요금제와 추천 요금제를 비교할 때, '이용 가능한 혜택'의 위치에 따른 유저의 만족도 및 실제 변경 완료율에 미치는 영향을 확인하고자 함)",
  },
};

function extractFileKey(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  try {
    const url = new URL(text);
    if (!/(^|\.)figma\.com$/i.test(url.hostname)) return '';
    const match = url.pathname.match(/^\/(?:file|design)\/([^/]+)/i);
    return match?.[1] || '';
  } catch {
    return '';
  }
}

const getFigmaNodeUrl = (file, flow) => {
  const baseUrl = file?.figma_file_link || (file?.figma_file_key ? `https://www.figma.com/design/${file.figma_file_key}` : '');
  if (!baseUrl) return '';
  try {
    const url = new URL(baseUrl);
    if (flow?.figma_start_node_id) {
      url.searchParams.set('node-id', String(flow.figma_start_node_id).replace(/:/g, '-'));
    }
    url.searchParams.set('hide-ui', '1');
    return url.toString();
  } catch {
    return '';
  }
};

const getFigmaEmbedUrl = (file, flow) => {
  const nodeUrl = getFigmaNodeUrl(file, flow);
  if (!nodeUrl) return '';
  return `https://www.figma.com/embed?embed_host=reopsai&url=${encodeURIComponent(nodeUrl)}`;
};

const formatCompactDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).replace(/\.\s?/g, '.').replace(/\.$/, '');
};

const formatFileSize = (bytes = 0) => {
  if (!bytes) return '0KB';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  return `${Math.ceil(bytes / 1024)}KB`;
};

const createInitialTestForm = () => ({
  name: '',
  description: '',
  source_type: '',
  source_url: '',
  device_type: '',
  scope_type: '',
  validation_type: 'single',
  figma_file_id: '',
  figma_flow_id: '',
  version_a_figma_file_id: '',
  version_a_figma_flow_id: '',
  version_b_figma_file_id: '',
  version_b_figma_flow_id: '',
  flow_goal: '',
  version_a_label: 'A',
  version_a_url: '',
  version_b_label: 'B',
  version_b_url: '',
  purpose: '',
  service_context: '',
  planning_brief: null,
});

const URL_VALIDATION_MESSAGE = '유효하지 않은 URL입니다.';

const hasValidUrlHostname = (hostname) => {
  const normalized = String(hostname || '').trim();
  if (!normalized) return false;
  if (normalized === 'localhost') return true;
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(normalized)) {
    return normalized.split('.').every((part) => Number(part) >= 0 && Number(part) <= 255);
  }
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(normalized);
};

const normalizeHttpUrlCandidate = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const getHttpUrlValidationError = (value) => {
  const trimmed = normalizeHttpUrlCandidate(value);
  if (!trimmed) return '';
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return URL_VALIDATION_MESSAGE;
    if (!hasValidUrlHostname(url.hostname)) return URL_VALIDATION_MESSAGE;
    return '';
  } catch {
    return URL_VALIDATION_MESSAGE;
  }
};

const isValidHttpUrl = (value) => !getHttpUrlValidationError(value);

const normalizeHttpUrlInput = (value) => {
  const trimmed = normalizeHttpUrlCandidate(value);
  if (!trimmed) return '';
  try {
    const url = new URL(trimmed);
    if (getHttpUrlValidationError(url.toString())) return '';
    url.hash = '';
    return url.toString();
  } catch {
    return '';
  }
};

const getPersonaFolderId = (persona) => persona?.folder_id || persona?.folderId || persona?.folder?.id || '';

const getPersonaProfile = (persona) => {
  const profile = persona?.profile || {};
  return {
    gender: persona?.gender || profile.gender || '성별 미상',
    age: persona?.age || profile.age,
    job: persona?.title || profile.job || profile.occupation || persona?.occupation || '직업 미상',
    personality:
      persona?.personality ||
      profile.personality ||
      persona?.personality_description ||
      '성격 정보가 아직 등록되지 않았습니다.',
    behavior:
      persona?.behaviours ||
      profile.behaviours ||
      persona?.behavior_pattern ||
      persona?.behavior_patterns ||
      profile.behavior_pattern ||
      profile.behavior_patterns ||
      persona?.lifestyle ||
      '행동 패턴 정보가 아직 등록되지 않았습니다.',
  };
};

function FigmaMark() {
  return (
    <span className="ux-create-figma-mark" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

function SelectCard({ active, icon: Icon, title, description, onClick, compact, figma }) {
  return (
    <button
      type="button"
      className={`ux-create-select-card${active ? ' is-active' : ''}${compact ? ' ux-create-select-card--compact' : ''}`}
      onClick={onClick}
    >
      <span className="ux-create-radio" aria-hidden="true" />
      <span className="ux-create-select-card__copy">
        <strong>
          {figma ? <FigmaMark /> : Icon ? <Icon size={20} /> : null}
          {title}
        </strong>
        {description ? <small>{description}</small> : null}
      </span>
    </button>
  );
}

function ProgressBar({ step }) {
  return (
    <div className="ux-create-progress">
      <div className="ux-create-progress__bar" aria-hidden="true">
        {[1, 2, 3, 4].map((item) => (
          <span key={item} className={item <= step ? 'is-filled' : ''} />
        ))}
      </div>
      <div className="ux-create-progress__count">
        <strong>{step}</strong>
        <span />
        <em>4</em>
      </div>
    </div>
  );
}

function TestGenerationProgress({ stages = getTestAnalysisSteps('ui', 'screen'), intervalMs = ANALYSIS_STAGE_INTERVAL_MS }) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 500);

    return () => window.clearInterval(timer);
  }, []);

  const activeStep = Math.min(stages.length - 1, Math.floor(elapsedMs / intervalMs));

  return (
    <GenerationProgressView
      stages={stages}
      currentStep={activeStep}
      ariaLabel="테스트 생성 진행 상태"
      notice="결과 생성까지 약 2~3분이 소요됩니다. 이 화면을 벗어나도 분석은 중단되지 않으며, 완료된 결과는 UX테스트 목록에서 확인하실 수 있습니다."
    />
  );
}

function SourceThumbnail({ item }) {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (item?.kind !== 'image' || !item.file) {
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(item.file);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [item]);

  if (item?.kind === 'url') {
    const capturedImageUrl = item.capturedImageUrl || item.captured_image_url || '';
    if (capturedImageUrl) {
      return <img src={personaAssetUrl(capturedImageUrl)} alt={item?.name || 'URL 화면'} />;
    }
    return <LinkIcon size={18} />;
  }

  if (previewUrl) {
    return <img src={previewUrl} alt={item?.name || '첨부 이미지'} />;
  }

  return <ImageIcon size={18} />;
}

function FigmaPreviewImage({ src, alt }) {
  const [objectUrl, setObjectUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const isStorageImage = /\/api\/persona\/storage\//i.test(String(src || ''));

  useEffect(() => {
    let cancelled = false;
    let nextObjectUrl = '';
    setFailed(false);
    setObjectUrl('');

    if (!src || !isStorageImage) {
      return undefined;
    }

    apiFetch(src, { method: 'GET' })
      .then((response) => {
        if (!response.ok) throw new Error(`이미지 요청 실패: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [isStorageImage, src]);

  const imageSrc = isStorageImage ? objectUrl : personaAssetUrl(src);
  if (!imageSrc || failed) {
    return (
      <div className="ux-figma-preview-card__empty">
        <ImageIcon size={18} />
      </div>
    );
  }
  return <img src={imageSrc} alt={alt || ''} onError={() => setFailed(true)} />;
}

function FigmaPreviewLoading({ fileName, flowName }) {
  const targetName = [flowName, fileName].filter(Boolean).join(' · ') || '선택한 Figma 프로토타입';
  const loadingSteps = ['파일 연결 확인', 'Flow 시작점 분석', '프레임 이미지 캐싱'];

  return (
    <div className="ux-figma-preview-loader" aria-live="polite">
      <div className="ux-figma-loader__head">
        <span className="ux-figma-loader__mark" aria-hidden="true">
          <FigmaMark />
        </span>
        <div>
          <strong>Figma 화면을 불러오고 있습니다.</strong>
          <p>{targetName}</p>
        </div>
        <Loader2 className="ux-figma-loader__spinner" size={20} />
      </div>

      <div className="ux-figma-loader__stage" aria-hidden="true">
        <div className="ux-figma-loader__browserbar">
          <i />
          <i />
          <i />
          <span />
        </div>
        <div className="ux-figma-loader__frames">
          <div className="ux-figma-loader__frame is-primary">
            <b />
            <span />
            <span />
          </div>
          <div className="ux-figma-loader__frame">
            <b />
            <span />
            <span />
          </div>
          <div className="ux-figma-loader__frame">
            <b />
            <span />
            <span />
          </div>
        </div>
        <div className="ux-figma-loader__scan" />
      </div>

      <ol className="ux-figma-loader__steps">
        {loadingSteps.map((step) => (
          <li key={step}>
            <span aria-hidden="true" />
            {step}
          </li>
        ))}
      </ol>
    </div>
  );
}

function UxCreateShell({ children, step, error, onBack, title, description, navigationGuard }) {
  return (
    <B2BLayout hideCollapseButton={false} defaultCollapsed={true} hidePageHeader={true} navigationGuard={navigationGuard}>
      <main className="ux-test-create-page">
        <header className="ux-test-create-title">
          <button type="button" className="ux-test-create-back" onClick={onBack} aria-label="목록으로 이동">
            <ChevronLeft size={24} />
          </button>
          <div>
            <h1>{title}</h1>
            <p>{description}</p>
          </div>
        </header>
        <ProgressBar step={step} />
        <ErrorBanner message={error} />
        {children}
      </main>
    </B2BLayout>
  );
}

function TestCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppContext();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') === 'ab' ? 'ab' : 'ui';
  const createCopy = {
    ...TEST_CREATE_COPY[type],
    ...getTestCreateCopy(user)[type],
  };
  const [folders, setFolders] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [figmaStatus, setFigmaStatus] = useState(null);
  const [figmaFiles, setFigmaFiles] = useState([]);
  const [figmaFlowsByFile, setFigmaFlowsByFile] = useState({});
  const [figmaFlows, setFigmaFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncingFigma, setSyncingFigma] = useState(false);
  const [error, setError] = useState('');
  const [ctaQuotaMessage, setCtaQuotaMessage] = useState('');
  const { remainingTokens, isQuotaExceeded, loading: quotaLoading } = useB2bTokenQuota();
  const quotaExceededMessage = useMemo(() => getQuotaExceededMessage(user), [user]);
  const [step, setStep] = useState(1);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState([]);
  const [collapsedPersonaGroupIds, setCollapsedPersonaGroupIds] = useState([]);
  const [sourceItems, setSourceItems] = useState([]);
  const [pendingUrl, setPendingUrl] = useState('');
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planOptionsLoading, setPlanOptionsLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [planBriefLoading, setPlanBriefLoading] = useState(false);
  const [figmaModalOpen, setFigmaModalOpen] = useState(false);
  const [figmaUrlInput, setFigmaUrlInput] = useState('');
  const [figmaModalError, setFigmaModalError] = useState('');
  const [figmaSelectOpenKey, setFigmaSelectOpenKey] = useState(null);
  const [figmaModalTarget, setFigmaModalTarget] = useState(null);
  const [figmaChangeConfirmOpen, setFigmaChangeConfirmOpen] = useState(false);
  const [figmaChanging, setFigmaChanging] = useState(false);
  const [figmaPreviewByFlow, setFigmaPreviewByFlow] = useState({});
  const [figmaPreviewLoadingByKey, setFigmaPreviewLoadingByKey] = useState({});
  const [figmaPreviewErrorsByKey, setFigmaPreviewErrorsByKey] = useState({});
  const [urlErrors, setUrlErrors] = useState({});
  const [pendingExit, setPendingExit] = useState(null);
  const [skipExitGuard, setSkipExitGuard] = useState(false);
  const [form, setForm] = useState(createInitialTestForm);
  const [uploadFile, setUploadFile] = useState(null);
  const [planOptions, setPlanOptions] = useState([]);
  const planOptionsLoadedRef = useRef(false);
  const personaGroupCollapseInitializedRef = useRef(false);
  const skipExitGuardRef = useRef(false);
  const popGuardArmedRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);
  const personaSelectionLimit = type === 'ab' ? AB_PERSONA_SELECTION_LIMIT : UI_PERSONA_SELECTION_LIMIT;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [personaData, folderData, statusData, files] = await Promise.all([
        personaApi.listPersonas({ limit: 100 }),
        personaApi.listFolders().catch(() => []),
        personaFigmaApi.getStatus().catch(() => null),
        personaFigmaApi.listFiles().catch(() => []),
      ]);
      setPersonas(personaData.items || []);
      setFolders(Array.isArray(folderData) ? folderData : []);
      setFigmaStatus(statusData);
      setFigmaFiles(files);
      const flowEntries = await Promise.all(
        files.map((file) => (
          personaFigmaApi.listFlows(file.id)
            .then((flows) => [file.id, flows])
            .catch(() => [file.id, []])
        ))
      );
      const nextFlowsByFile = Object.fromEntries(flowEntries);
      setFigmaFlowsByFile(nextFlowsByFile);
      setForm((prev) => {
        const defaultFileId = files[0]?.id ? String(files[0].id) : '';
        const defaultFlowId = defaultFileId && nextFlowsByFile[defaultFileId]?.[0]?.id
          ? String(nextFlowsByFile[defaultFileId][0].id)
          : '';
        if (!defaultFileId) return prev;
        const next = { ...prev };
        if (!next.figma_file_id) next.figma_file_id = defaultFileId;
        if (!next.figma_flow_id && defaultFlowId) next.figma_flow_id = defaultFlowId;
        if (type === 'ab') {
          if (!next.version_a_figma_file_id) next.version_a_figma_file_id = defaultFileId;
          if (!next.version_a_figma_flow_id && defaultFlowId) next.version_a_figma_flow_id = defaultFlowId;
          if (!next.version_b_figma_file_id) next.version_b_figma_file_id = defaultFileId;
          if (!next.version_b_figma_flow_id && defaultFlowId) next.version_b_figma_flow_id = defaultFlowId;
        }
        return next;
      });
    } catch (err) {
      setError(err.message || '생성 화면 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!form.figma_file_id) {
      setFigmaFlows([]);
      return;
    }
    const cachedFlows = figmaFlowsByFile[form.figma_file_id];
    if (cachedFlows) {
      setFigmaFlows(cachedFlows);
      if (!form.figma_flow_id && cachedFlows[0]?.id) {
        setForm((prev) => ({ ...prev, figma_flow_id: String(cachedFlows[0].id) }));
      }
      return;
    }
    personaFigmaApi.listFlows(form.figma_file_id)
      .then((flows) => {
        setFigmaFlows(flows);
        setFigmaFlowsByFile((prev) => ({ ...prev, [form.figma_file_id]: flows }));
        if (!form.figma_flow_id && flows[0]?.id) {
          setForm((prev) => ({ ...prev, figma_flow_id: String(flows[0].id) }));
        }
      })
      .catch(() => setFigmaFlows([]));
  }, [figmaFlowsByFile, form.figma_file_id, form.figma_flow_id]);

  const personaGroups = useMemo(() => {
    const sortedFolders = sortPersonaFolders(folders);
    const folderGroups = sortedFolders.map((folder) => {
      const folderId = String(folder.id);
      const items = personas.filter((persona) => String(getPersonaFolderId(persona)) === folderId);
      return {
        id: folderId,
        name: folder.name || '퍼소나 그룹',
        count: folder.persona_count ?? folder.personas_count ?? items.length,
        items,
      };
    }).filter((group) => group.items.length > 0);

    const groupedIds = new Set(folderGroups.flatMap((group) => group.items.map((persona) => String(persona.id))));
    const ungrouped = personas.filter((persona) => !groupedIds.has(String(persona.id)));
    if (ungrouped.length > 0) {
      folderGroups.push({
        id: '__ungrouped__',
        name: '그룹없음',
        count: ungrouped.length,
        items: ungrouped,
      });
    }

    return folderGroups;
  }, [folders, personas]);

  useEffect(() => {
    if (personaGroupCollapseInitializedRef.current || personaGroups.length === 0) return;
    setCollapsedPersonaGroupIds(personaGroups.map((group) => String(group.id)));
    personaGroupCollapseInitializedRef.current = true;
  }, [personaGroups]);

  const isFigmaConnected = Boolean(figmaStatus?.connected || figmaStatus?.account);
  const figmaAccount = figmaStatus?.account || {};
  const figmaAccountName = figmaAccount.figma_handle || figmaAccount.handle || figmaAccount.name || 'Figma 계정';
  const figmaAccountEmail = figmaAccount.figma_email || figmaAccount.email || '-';
  const figmaAccountAvatar = figmaAccount.avatar_url || figmaAccount.avatarUrl || figmaAccount.photo_url || '';
  const figmaPrototypeOptions = useMemo(() => (
    figmaFiles.flatMap((file) => (
      (figmaFlowsByFile[file.id] || []).map((flow) => ({ file, flow }))
    ))
  ), [figmaFiles, figmaFlowsByFile]);
  const getFigmaFieldNames = (variant = null) => (
    variant
      ? {
        file: `version_${variant.toLowerCase()}_figma_file_id`,
        flow: `version_${variant.toLowerCase()}_figma_flow_id`,
      }
      : { file: 'figma_file_id', flow: 'figma_flow_id' }
  );
  const getFigmaSelectionIds = (variant = null) => {
    const fields = getFigmaFieldNames(variant);
    return {
      fileId: form[fields.file] || '',
      flowId: form[fields.flow] || '',
    };
  };
  const getSelectedFigmaPrototype = (variant = null) => {
    const { fileId, flowId } = getFigmaSelectionIds(variant);
    return figmaPrototypeOptions.find(({ file, flow }) => (
      String(file.id) === String(fileId) && String(flow.id) === String(flowId)
    ));
  };
  const getSelectedFigmaFile = (variant = null) => {
    const { fileId } = getFigmaSelectionIds(variant);
    return getSelectedFigmaPrototype(variant)?.file || figmaFiles.find((file) => String(file.id) === String(fileId));
  };
  const getSelectedFigmaFlow = (variant = null) => {
    const { fileId, flowId } = getFigmaSelectionIds(variant);
    return getSelectedFigmaPrototype(variant)?.flow || (figmaFlowsByFile[fileId] || figmaFlows).find((flow) => String(flow.id) === String(flowId));
  };
  const setFigmaSelection = (variant, fileId, flowId) => {
    const fields = getFigmaFieldNames(variant);
    setForm((prev) => ({
      ...prev,
      [fields.file]: String(fileId),
      [fields.flow]: String(flowId),
    }));
  };
  const getFigmaPreviewKey = (variant = null) => {
    const { fileId, flowId } = getFigmaSelectionIds(variant);
    return fileId && flowId ? `${fileId}:${flowId}` : '';
  };
  const getFlowScreenLimit = () => (type === 'ab' ? AB_FLOW_SOURCE_ITEMS_LIMIT : FLOW_SOURCE_ITEMS_LIMIT);
  const getSourceScreenLimit = () => (form.scope_type === 'flow' ? getFlowScreenLimit() : SINGLE_SOURCE_ITEMS_LIMIT);
  const getSelectedFigmaPreview = (variant = null) => {
    const key = getFigmaPreviewKey(variant);
    return key ? figmaPreviewByFlow[key] : null;
  };
  const getFigmaPreviewOwnFlowScreenLimit = (variant = null) => {
    const preview = getSelectedFigmaPreview(variant);
    const transitionCount = Array.isArray(preview?.transitions) ? preview.transitions.length : 0;
    const flowLimit = getFlowScreenLimit();
    return Math.min(flowLimit, transitionCount > 0 ? transitionCount + 1 : flowLimit);
  };
  const getFigmaPreviewSharedFlowScreenLimit = (variant = null) => {
    const ownLimit = getFigmaPreviewOwnFlowScreenLimit(variant);
    if (type !== 'ab' || form.source_type !== 'figma' || form.scope_type !== 'flow') {
      return ownLimit;
    }
    const variantScreenCounts = ['A', 'B'].map((nextVariant) => {
      const preview = getSelectedFigmaPreview(nextVariant);
      if (!Array.isArray(preview?.screens)) return 0;
      return Math.min(preview.screens.length, getFigmaPreviewOwnFlowScreenLimit(nextVariant));
    });
    if (variantScreenCounts.some((count) => count <= 0)) {
      return ownLimit;
    }
    return Math.min(ownLimit, ...variantScreenCounts, getFlowScreenLimit());
  };
  const getSelectedFigmaPreviewScreens = (variant = null) => {
    const preview = getSelectedFigmaPreview(variant);
    if (!Array.isArray(preview?.screens)) return [];
    if (form.scope_type !== 'flow') {
      const screenLimit = type === 'ab' ? 1 : getSourceScreenLimit();
      return preview.screens.slice(0, screenLimit);
    }
    const flowLimit = getFigmaPreviewSharedFlowScreenLimit(variant);
    return preview.screens.slice(0, flowLimit);
  };
  const getSelectedFigmaPreviewTransitions = (variant = null) => {
    const preview = getSelectedFigmaPreview(variant);
    if (!Array.isArray(preview?.transitions)) return [];
    if (form.scope_type !== 'flow') {
      const screenLimit = type === 'ab' ? 1 : getSourceScreenLimit();
      return preview.transitions.slice(0, Math.max(screenLimit - 1, 0));
    }
    const flowLimit = getFigmaPreviewSharedFlowScreenLimit(variant);
    return preview.transitions.slice(0, Math.max(flowLimit - 1, 0));
  };
  const selectedFigmaFile = getSelectedFigmaFile();
  const selectedFigmaFlow = getSelectedFigmaFlow();
  const selectedFigmaPreview = getSelectedFigmaPreview();
  const selectedSourceLabel = SOURCE_OPTIONS.find((option) => option.value === form.source_type)?.label || '-';
  const selectedScopeLabel = form.scope_type === 'flow' ? '사용자 플로우' : form.scope_type === 'screen' ? '단일 화면' : '-';
  const selectedDeviceLabel = form.device_type === 'mobile' ? 'Mobile' : form.device_type === 'pc' ? 'PC' : '-';
  const getSourceItems = (variant) => (
    variant
      ? sourceItems.filter((item) => item.variant === variant)
      : sourceItems.filter((item) => !item.variant)
  );
  const isImageSourceKind = (kind) => kind === 'image' || kind === '이미지';
  const isUrlSourceKind = (kind) => kind === 'url' || kind === 'URL';
  const isAbSingleScreenImageSource = (kind = form.source_type) => (
    type === 'ab' &&
    form.scope_type === 'screen' &&
    isImageSourceKind(kind)
  );
  const isAbSingleScreenUrlSource = (kind = form.source_type) => (
    type === 'ab' &&
    form.scope_type === 'screen' &&
    isUrlSourceKind(kind)
  );
  const isAbFlowImageSource = (kind = form.source_type) => (
    type === 'ab' &&
    form.scope_type === 'flow' &&
    isImageSourceKind(kind)
  );
  const isAbFlowUrlSource = (kind = form.source_type) => (
    type === 'ab' &&
    form.scope_type === 'flow' &&
    isUrlSourceKind(kind)
  );
  const getSourceLimit = (kind = form.source_type) => {
    if (isAbSingleScreenImageSource(kind) || isAbSingleScreenUrlSource(kind)) {
      return { min: 1, max: 1 };
    }
    const max = getSourceScreenLimit();
    if (type === 'ab' && form.scope_type === 'flow') {
      return { min: MIN_FLOW_SOURCE_ITEMS, max };
    }
    return { min: 1, max };
  };
  const getSourceLimitMessage = (kind, reason = 'requirement') => {
    if (isAbSingleScreenImageSource(kind)) {
      return '단일 화면은 1개의 image만 첨부할 수 있습니다.';
    }
    if (isAbSingleScreenUrlSource(kind)) {
      return '단일 화면은 1개의 URL만 첨부할 수 있습니다.';
    }
    if (isAbFlowImageSource(kind)) {
      return `사용자 플로우는 Version별 image를 최소 ${MIN_FLOW_SOURCE_ITEMS}개 이상 최대 ${AB_FLOW_SOURCE_ITEMS_LIMIT}개 이하로 첨부해야 합니다.`;
    }
    if (isAbFlowUrlSource(kind)) {
      return `사용자 플로우는 Version별 URL을 최소 ${MIN_FLOW_SOURCE_ITEMS}개 이상 최대 ${AB_FLOW_SOURCE_ITEMS_LIMIT}개 이하로 첨부해야 합니다.`;
    }
    return '';
  };
  const sourceRequirementLabel = (kind) => {
    const limitMessage = getSourceLimitMessage(kind);
    if (limitMessage) return limitMessage;
    const itemLabel = kind === 'URL' ? 'URL' : '이미지';
    const fileSizeText = kind === 'image' || kind === '이미지' ? ' · 파일당 10MB 이하' : '';
    const limit = getSourceLimit(kind);
    if (type === 'ui') {
      return `${itemLabel}는 최대 ${limit.max}개까지 추가할 수 있습니다.${fileSizeText}`;
    }
    if (form.scope_type === 'flow') {
      return `사용자 플로우는 Version별 ${itemLabel}가 ${limit.min}개 이상 ${limit.max}개 이하로 필요합니다.${fileSizeText}`;
    }
    return `단일 화면은 Version별 ${itemLabel}가 ${limit.min}개 이상 ${limit.max}개 이하로 필요합니다.${fileSizeText}`;
  };
  const getSourceCount = (kind, variant = '') => (
    getSourceItems(variant).filter((item) => item.kind === kind).length
  );
  const isSourceLimitReached = (kind, variant = '') => {
    return getSourceCount(kind, variant) >= getSourceLimit(kind).max;
  };
  const getSourceValidationError = () => {
    if (!form.scope_type) return '테스트 범위를 선택해 주세요.';
    if (!form.source_type) return '화면 제공 방식을 선택해 주세요.';
    if (form.source_type === 'figma') {
      if (!isFigmaConnected) return 'Figma 계정을 먼저 연결해 주세요.';
      if (type === 'ab') {
        const missingVariant = ['A', 'B'].find((variant) => {
          const { fileId, flowId } = getFigmaSelectionIds(variant);
          return !fileId || !flowId;
        });
        return missingVariant ? `Version ${missingVariant}의 Figma 프로토타입을 선택해 주세요.` : '';
      }
      return form.figma_file_id && form.figma_flow_id ? '' : 'Figma 프로토타입을 선택해 주세요.';
    }

    const kind = form.source_type;
    const limit = getSourceLimit(kind);
    const validateItems = (items, label) => {
      const matchingItems = items.filter((item) => item.kind === kind);
      if (kind === 'image') {
        const oversized = matchingItems.find((item) => (item.file?.size || 0) > MAX_IMAGE_FILE_SIZE_BYTES);
        if (oversized) return `${label}의 "${oversized.file.name}" 파일이 10MB를 초과했습니다.`;
      }
      if (kind === 'url') {
        const invalidUrl = matchingItems.find((item) => !isValidHttpUrl(item.url));
        if (invalidUrl) return `${label}에 올바르지 않은 URL이 있습니다. 올바른 URL을 입력해 주세요.`;
      }
      if (matchingItems.length < limit.min) {
        return getSourceLimitMessage(kind, 'min') || `${label}는 ${kind === 'url' ? 'URL' : '이미지'}를 최소 ${limit.min}개 추가해 주세요.`;
      }
      if (matchingItems.length > limit.max) {
        return getSourceLimitMessage(kind, 'max') || `${label}는 ${kind === 'url' ? 'URL' : '이미지'}를 최대 ${limit.max}개까지만 추가할 수 있습니다.`;
      }
      return '';
    };

    if (type === 'ab') {
      for (const variant of ['A', 'B']) {
        const errorMessage = validateItems(getSourceItems(variant), `Version ${variant}`);
        if (errorMessage) return errorMessage;
      }
      if (form.scope_type === 'flow' && (isImageSourceKind(kind) || isUrlSourceKind(kind))) {
        const versionACount = getSourceCount(kind, 'A');
        const versionBCount = getSourceCount(kind, 'B');
        if (versionACount !== versionBCount) {
          const itemLabel = isUrlSourceKind(kind) ? 'URL' : 'image';
          return `사용자 플로우는 Version A와 Version B의 ${itemLabel} 개수가 동일해야 합니다.`;
        }
      }
      return '';
    }
    return validateItems(getSourceItems(), '화면 사용성 검증');
  };

  useEffect(() => {
    if (form.source_type !== 'figma') {
      setFigmaPreviewLoadingByKey({});
      setFigmaPreviewErrorsByKey({});
      return undefined;
    }
    const selectionInputs = type === 'ab'
      ? [
        { fileId: form.version_a_figma_file_id, flowId: form.version_a_figma_flow_id },
        { fileId: form.version_b_figma_file_id, flowId: form.version_b_figma_flow_id },
      ]
      : [{ fileId: form.figma_file_id, flowId: form.figma_flow_id }];
    const selections = selectionInputs
      .map(({ fileId, flowId }) => (fileId && flowId ? { fileId, flowId, key: `${fileId}:${flowId}` } : null))
      .filter(Boolean)
      .filter(({ key }, index, rows) => rows.findIndex((row) => row.key === key) === index);
    if (selections.length === 0) {
      return undefined;
    }
    const missingSelections = selections.filter(({ key }) => !figmaPreviewByFlow[key]);
    if (missingSelections.length === 0) return undefined;

    let cancelled = false;
    missingSelections.forEach(({ fileId, flowId, key }) => {
      setFigmaPreviewLoadingByKey((prev) => ({ ...prev, [key]: true }));
      setFigmaPreviewErrorsByKey((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      personaFigmaApi.previewFlow(fileId, flowId)
        .then((preview) => {
          if (cancelled) return;
          setFigmaPreviewByFlow((prev) => ({ ...prev, [key]: preview || {} }));
        })
        .catch((err) => {
          if (!cancelled) {
            setFigmaPreviewErrorsByKey((prev) => ({
              ...prev,
              [key]: err.message || 'Figma 프로토타입 이미지를 불러오지 못했습니다.',
            }));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setFigmaPreviewLoadingByKey((prev) => ({ ...prev, [key]: false }));
          }
        });
    });

    return () => {
      cancelled = true;
    };
  }, [
    figmaPreviewByFlow,
    form.figma_file_id,
    form.figma_flow_id,
    form.source_type,
    form.version_a_figma_file_id,
    form.version_a_figma_flow_id,
    form.version_b_figma_file_id,
    form.version_b_figma_flow_id,
    type,
  ]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openPlanModal = async () => {
    setPlanModalOpen(true);
    if (planOptionsLoadedRef.current) return;
    setPlanOptionsLoading(true);
    try {
      const response = await getWorkspaceArtifactOptions({ artifactTypes: ['plan'] });
      setPlanOptions(response.items || []);
      planOptionsLoadedRef.current = true;
    } catch (loadError) {
      setError(loadError.message || '리서치 계획서를 불러오지 못했습니다.');
    } finally {
      setPlanOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (planModalOpen && !selectedPlanId && planOptions[0]) {
      setSelectedPlanId(planOptions[0].id);
    }
  }, [planModalOpen, planOptions, selectedPlanId]);

  const handleLoadPlanIntoTest = async () => {
    const selectedPlan = planOptions.find((plan) => String(plan.id) === String(selectedPlanId));
    if (!selectedPlan) return;
    try {
      setPlanBriefLoading(true);
      setError('');
      const artifact = await getWorkspaceArtifactContent(selectedPlan.id);
      const content = artifact.item?.content || '';
      const fallback = mapPlanContentToTestForm({ ...selectedPlan, content });
      let context = null;
      try {
        context = await personaTestApi.generateContextFromPlan({
          planContent: content,
          studyName: selectedPlan.title || selectedPlan.studyName,
          testType: type,
        });
      } catch {
        context = null;
      }
      setForm((prev) => ({
        ...prev,
        name: String(context?.name || fallback.name || prev.name).slice(0, 60),
        description: context?.description || fallback.description || prev.description,
        purpose: context?.purpose || fallback.purpose || prev.purpose,
        service_context: context?.serviceContext || fallback.service_context || prev.service_context,
        // 계획서의 과업 제안은 테스트 맥락으로만 반영한다.
        // Flow 완료 목표는 사용자가 별도로 입력한 값만 유지한다.
        flow_goal: prev.flow_goal,
        planning_brief: {
          testType: type,
          userTask: context?.userTask || '',
          observationPoints: context?.observationPoints || [],
          comparisonDecision: context?.comparisonDecision || '',
          decisionCriteria: context?.decisionCriteria || [],
        },
      }));
      setPlanModalOpen(false);
    } catch (loadError) {
      setError(resolvePersonaApiErrorMessage(loadError, '계획서를 불러오지 못했습니다.'));
    } finally {
      setPlanBriefLoading(false);
    }
  };

  const clearSourceInputs = useCallback(() => {
    setSourceItems([]);
    setPendingUrl('');
    setUploadFile(null);
    setUrlErrors({});
    setFigmaSelectOpenKey(null);
  }, []);

  const resetStepTwoAndLater = useCallback(() => {
    clearSourceInputs();
    setSelectedPersonaIds([]);
    setForm((prev) => ({
      ...prev,
      scope_type: '',
      source_type: '',
      source_url: '',
      figma_file_id: '',
      figma_flow_id: '',
      version_a_figma_file_id: '',
      version_a_figma_flow_id: '',
      version_b_figma_file_id: '',
      version_b_figma_flow_id: '',
      version_a_url: '',
      version_b_url: '',
      flow_goal: '',
      purpose: '',
      service_context: '',
    }));
  }, [clearSourceInputs]);

  const resetStepThreeAndLater = useCallback(() => {
    setSelectedPersonaIds([]);
  }, []);

  const applyPreviousStep = useCallback((targetStep) => {
    if (targetStep <= 1) {
      resetStepTwoAndLater();
    } else if (targetStep <= 2) {
      resetStepThreeAndLater();
    }
    setStep(targetStep);
  }, [resetStepThreeAndLater, resetStepTwoAndLater]);

  const setUrlError = (key, message) => {
    setUrlErrors((prev) => ({ ...prev, [key || 'default']: message }));
  };

  const clearUrlError = (key) => {
    setUrlErrors((prev) => {
      const next = { ...prev };
      delete next[key || 'default'];
      return next;
    });
  };

  const hasDraftChanges = useMemo(() => (
    Boolean(
      form.name.trim() ||
      form.description.trim() ||
      form.device_type ||
      form.scope_type ||
      form.source_type ||
      form.source_url ||
      form.flow_goal.trim() ||
      form.version_a_url.trim() ||
      form.version_b_url.trim() ||
      pendingUrl.trim() ||
      sourceItems.length > 0 ||
      selectedPersonaIds.length > 0 ||
      step > 1
    )
  ), [form, pendingUrl, selectedPersonaIds.length, sourceItems.length, step]);

  const disableExitGuard = useCallback(() => {
    skipExitGuardRef.current = true;
    setSkipExitGuard(true);
  }, []);

  const requestExit = useCallback((action) => {
    if (!hasDraftChanges || skipExitGuard || skipExitGuardRef.current) return true;
    setPendingExit(action);
    return false;
  }, [hasDraftChanges, skipExitGuard]);

  const handleNavigationGuard = useCallback((targetPath = '/tests', proceed) => (
    requestExit({ type: 'navigate', targetPath, proceed })
  ), [requestExit]);

  const handleBackToList = useCallback(() => {
    if (requestExit({ type: 'navigate', targetPath: '/tests' })) {
      navigate('/tests');
    }
  }, [navigate, requestExit]);

  const handlePreviousStep = useCallback((targetStep) => {
    if (!requestExit({ type: 'step', targetStep })) return;
    applyPreviousStep(targetStep);
  }, [applyPreviousStep, requestExit]);

  const handleCancelPendingExit = () => {
    if (pendingExit?.restoreHistoryOnCancel) {
      window.history.pushState({ reopsaiCreateGuard: true }, '', window.location.href);
    }
    setPendingExit(null);
  };

  const handleConfirmPendingExit = () => {
    const action = pendingExit;
    setPendingExit(null);
    if (!action) return;
    if (action.type === 'step') {
      applyPreviousStep(action.targetStep);
      return;
    }
    if (typeof action.proceed === 'function') {
      disableExitGuard();
      action.proceed();
      return;
    }
    disableExitGuard();
    navigate(action.targetPath || '/tests', { replace: action.replace });
  };

  useEffect(() => {
    if (!hasDraftChanges || skipExitGuard) {
      popGuardArmedRef.current = false;
      return undefined;
    }
    if (!popGuardArmedRef.current) {
      window.history.pushState({ reopsaiCreateGuard: true }, '', window.location.href);
      popGuardArmedRef.current = true;
    }
    const handlePopState = () => {
      if (!hasDraftChanges || skipExitGuard || skipExitGuardRef.current) return;
      setPendingExit({
        type: 'navigate',
        targetPath: '/tests',
        replace: true,
        restoreHistoryOnCancel: true,
      });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasDraftChanges, navigate, skipExitGuard]);

  const togglePersona = (personaId) => {
    const value = String(personaId);
    setSelectedPersonaIds((prev) => {
      if (prev.includes(value)) {
        return prev.filter((id) => id !== value);
      }
      if (prev.length >= personaSelectionLimit) {
        return prev;
      }
      setError('');
      return [...prev, value];
    });
  };

  const togglePersonaGroup = (groupId) => {
    const value = String(groupId);
    setCollapsedPersonaGroupIds((current) => (
      current.map(String).includes(value)
        ? current.filter((id) => String(id) !== value)
        : [...current, value]
    ));
  };

  const handleSourceChange = (sourceType) => {
    clearSourceInputs();
    setForm((prev) => ({
      ...prev,
      source_type: sourceType,
      source_url: '',
      figma_file_id: '',
      figma_flow_id: '',
      version_a_figma_file_id: '',
      version_a_figma_flow_id: '',
      version_b_figma_file_id: '',
      version_b_figma_flow_id: '',
      version_a_url: '',
      version_b_url: '',
    }));
  };

  const handleScopeChange = (scopeType) => {
    clearSourceInputs();
    setSelectedPersonaIds([]);
    setForm((prev) => ({
      ...prev,
      scope_type: scopeType,
      source_type: '',
      source_url: '',
      figma_file_id: '',
      figma_flow_id: '',
      version_a_figma_file_id: '',
      version_a_figma_flow_id: '',
      version_b_figma_file_id: '',
      version_b_figma_flow_id: '',
      version_a_url: '',
      version_b_url: '',
      flow_goal: scopeType === 'flow' ? prev.flow_goal : '',
    }));
  };

  const handleImageFiles = (files, variant = '') => {
    const fileList = Array.from(files || []).filter((file) => file.type.startsWith('image/'));
    if (fileList.length === 0) {
      setError('이미지 파일만 첨부할 수 있습니다.');
      return;
    }
    const validFiles = fileList.filter((file) => file.size <= MAX_IMAGE_FILE_SIZE_BYTES);
    const oversizedFiles = fileList.filter((file) => file.size > MAX_IMAGE_FILE_SIZE_BYTES);
    const limit = getSourceLimit('image');
    const existingCount = getSourceCount('image', variant);
    const availableSlots = Math.max(limit.max - existingCount, 0);
    const nextFiles = validFiles.slice(0, availableSlots);
    const notices = [];

    if (oversizedFiles.length > 0) {
      notices.push(`10MB를 초과한 이미지 ${oversizedFiles.length}개는 제외했습니다.`);
    }
    if (availableSlots === 0) {
      notices.push(getSourceLimitMessage('image', 'max') || `이미지는 최대 ${limit.max}개까지 추가할 수 있습니다.`);
    } else if (validFiles.length > nextFiles.length) {
      notices.push(getSourceLimitMessage('image', 'max') || `${sourceRequirementLabel('이미지')} 초과분은 제외했습니다.`);
    }
    if (notices.length > 0) {
      setError(notices.join(' '));
    } else {
      setError('');
    }
    if (nextFiles.length === 0) return;
    if (!variant) {
      setUploadFile(nextFiles[0]);
    }
    setSourceItems((prev) => {
      const scopedItems = variant ? prev.filter((item) => item.variant === variant) : prev.filter((item) => !item.variant);
      const additions = nextFiles.map((file, index) => ({
        id: `${Date.now()}-${variant || 'screen'}-${file.name}-${index}`,
        kind: 'image',
        variant,
        name: `화면${scopedItems.length + index + 1}`,
        file,
      }));
      return [...prev, ...additions];
    });
  };

  const handleAddUrl = (variant = '') => {
    const sourceVariant = typeof variant === 'string' ? variant : '';
    const errorKey = sourceVariant || 'default';
    const sourceField = sourceVariant ? `version_${sourceVariant.toLowerCase()}_url` : '';
    const rawUrl = (sourceVariant ? form[sourceField] : pendingUrl).trim();
    if (!rawUrl) return;
    const validationError = getHttpUrlValidationError(rawUrl);
    if (validationError) {
      setUrlError(errorKey, validationError);
      return;
    }
    const url = normalizeHttpUrlInput(rawUrl);
    if (!url) {
      setUrlError(errorKey, URL_VALIDATION_MESSAGE);
      return;
    }
    const limit = getSourceLimit('url');
    const existingCount = getSourceCount('url', sourceVariant);
    if (existingCount >= limit.max) {
      setUrlError(errorKey, getSourceLimitMessage('URL', 'max') || `URL은 최대 ${limit.max}개까지 추가할 수 있습니다.`);
      return;
    }
    const normalizedExistingUrls = getSourceItems(sourceVariant)
      .filter((item) => item.kind === 'url')
      .map((item) => normalizeHttpUrlInput(item.url));
    if (normalizedExistingUrls.includes(url)) {
      setUrlError(errorKey, '이미 추가된 URL입니다.');
      return;
    }
    const itemId = `${Date.now()}-${sourceVariant || 'screen'}-${url}`;
    if (!sourceVariant) {
      setField('source_url', form.source_url || url);
    }
    setSourceItems((prev) => {
      const scopedItems = sourceVariant ? prev.filter((item) => item.variant === sourceVariant) : prev.filter((item) => !item.variant);
      const addition = {
        id: itemId,
        kind: 'url',
        variant: sourceVariant,
        name: `화면${scopedItems.length + 1}`,
        url,
        captureLoading: true,
      };
      return [...prev, addition];
    });
    setError('');
    clearUrlError(errorKey);
    if (sourceVariant) {
      setField(sourceField, '');
    } else {
      setPendingUrl('');
    }
    personaTestApi.captureUrl(url)
      .then((captured) => {
        const capturedImageUrl = captured?.capturedImageUrl || captured?.captured_image_url || '';
        setSourceItems((prev) => prev.map((item) => (
          item.id === itemId
            ? {
              ...item,
              pageTitle: captured?.title || captured?.pageTitle || captured?.page_title || item.pageTitle,
              page_title: captured?.title || captured?.pageTitle || captured?.page_title || item.pageTitle,
              capturedImageUrl,
              captured_image_url: capturedImageUrl,
              captureBackend: captured?.capture_backend || captured?.captureBackend,
              captureStatusCode: captured?.status_code || captured?.captureStatusCode,
              captureLoading: false,
            }
            : item
        )));
      })
      .catch(() => {
        setSourceItems((prev) => prev.map((item) => (
          item.id === itemId ? { ...item, captureLoading: false } : item
        )));
      });
  };

  const updateSourceItemName = (itemId, name) => {
    setSourceItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, name } : item)));
  };

  const removeSourceItem = (itemId) => {
    setSourceItems((prev) => {
      const next = prev.filter((item) => item.id !== itemId);
      if (form.source_type === 'image') {
        setUploadFile(next.find((item) => item.kind === 'image' && !item.variant)?.file || null);
      }
      if (form.source_type === 'url') {
        setField('source_url', next.find((item) => item.kind === 'url' && !item.variant)?.url || '');
      }
      return next;
    });
  };

  const handleConnectFigma = async () => {
    setError('');
    try {
      window.sessionStorage?.setItem(TEST_FIGMA_RETURN_PATH_KEY, `${location.pathname}${location.search}`);
      const url = await personaFigmaApi.getConnectUrl();
      if (url) {
        disableExitGuard();
        window.location.assign(url);
      }
    } catch (err) {
      setError(err.message || 'Figma 연결 URL을 가져오지 못했습니다.');
    }
  };

  const handleRequestFigmaChange = () => {
    setFigmaChangeConfirmOpen(true);
  };

  const handleConfirmFigmaChange = async () => {
    if (figmaChanging) return;
    setFigmaChanging(true);
    setError('');
    try {
      await personaFigmaApi.disconnect();
      setFigmaStatus(null);
      setFigmaFiles([]);
      setFigmaFlows([]);
      setFigmaFlowsByFile({});
      setFigmaPreviewByFlow({});
      setFigmaPreviewLoadingByKey({});
      setFigmaPreviewErrorsByKey({});
      setFigmaSelectOpenKey(null);
      setFigmaModalOpen(false);
      setFigmaModalTarget(null);
      setFigmaUrlInput('');
      setFigmaModalError('');
      setForm((prev) => ({
        ...prev,
        figma_file_id: '',
        figma_flow_id: '',
        version_a_figma_file_id: '',
        version_a_figma_flow_id: '',
        version_b_figma_file_id: '',
        version_b_figma_flow_id: '',
      }));
      setFigmaChangeConfirmOpen(false);
    } catch (err) {
      setError(err.message || 'Figma 계정 변경에 실패했습니다.');
    } finally {
      setFigmaChanging(false);
    }
  };

  const handleSyncFigmaFile = async (event) => {
    event.preventDefault();
    const figmaFileKey = extractFileKey(figmaUrlInput);
    if (!figmaFileKey) {
      setFigmaModalError('올바른 Figma 파일 URL 형식이 아닙니다.');
      return;
    }
    setSyncingFigma(true);
    setError('');
    setFigmaModalError('');
    try {
      const figmaFileLink = figmaUrlInput.trim();
      const synced = await personaFigmaApi.syncFile({
        figma_file_key: figmaFileKey,
        figma_file_name: figmaFileKey,
        figma_file_link: figmaFileLink,
      });
      setFigmaUrlInput('');
      setFigmaModalOpen(false);
      await loadData();
      if (synced?.id) {
        const flowId = synced.flows?.[0]?.id ? String(synced.flows[0].id) : '';
        if (flowId) {
          setFigmaSelection(figmaModalTarget, String(synced.id), flowId);
        } else {
          const fields = getFigmaFieldNames(figmaModalTarget);
          setField(fields.file, String(synced.id));
        }
      }
      setFigmaModalTarget(null);
    } catch (err) {
      setFigmaModalError(err.message || 'Figma 파일 연결에 실패했습니다.');
    } finally {
      setSyncingFigma(false);
    }
  };

  const captureUrlPreview = async (entry) => {
    if (!entry?.url || entry.capturedImageUrl || entry.captured_image_url) return entry;
    try {
      const captured = await personaTestApi.captureUrl(entry.url);
      const capturedImageUrl = captured?.capturedImageUrl || captured?.captured_image_url || '';
      if (!capturedImageUrl) throw new Error('URL 캡처 이미지가 생성되지 않았습니다.');
      return {
        ...entry,
        pageTitle: captured?.title || captured?.pageTitle || captured?.page_title || entry.pageTitle,
        page_title: captured?.title || captured?.pageTitle || captured?.page_title || entry.pageTitle,
        capturedImageUrl,
        captured_image_url: capturedImageUrl,
        captureBackend: captured?.capture_backend || captured?.captureBackend,
        captureStatusCode: captured?.status_code || captured?.captureStatusCode,
      };
    } catch (err) {
      throw new Error(`${entry.url} 화면을 캡처하지 못했습니다. ${err.message || 'URL 접근 상태를 확인해 주세요.'}`);
    }
  };

  const buildFigmaSourceEntry = (variant = null) => {
    const figmaFile = getSelectedFigmaFile(variant);
    const figmaFlow = getSelectedFigmaFlow(variant);
    const { fileId, flowId } = getFigmaSelectionIds(variant);
    const nodeUrl = getFigmaNodeUrl(figmaFile, figmaFlow);
    const figmaEmbedUrl = getFigmaEmbedUrl(figmaFile, figmaFlow);
    const label = variant ? `Version ${variant}` : 'Figma 프로토타입';
    return {
      id: variant ? `${variant.toLowerCase()}-figma-flow` : 'figma-flow',
      key: variant,
      label,
      name: figmaFlow?.figma_flow_name || figmaFile?.figma_file_name || label,
      figma_file_id: fileId || null,
      figma_file: figmaFile || null,
      figma_flow_id: flowId || null,
      figma_flow: figmaFlow || null,
      figma_node_url: nodeUrl,
      figmaNodeUrl: nodeUrl,
      figma_embed_url: figmaEmbedUrl,
      figmaEmbedUrl,
      thumbnail_url: figmaFile?.thumbnail_url || '',
      thumbnailUrl: figmaFile?.thumbnail_url || '',
    };
  };

  const getSelectedFigmaScreenEntries = (variant = null) => {
    const base = buildFigmaSourceEntry(variant);
    const previewScreens = getSelectedFigmaPreviewScreens(variant);
    if (!previewScreens.length) {
      return [base];
    }
    return previewScreens.map((screen, index) => {
      const imageUrl = screen.imageUrl || screen.image_url || '';
      const figmaNodeId = screen.figmaNodeId || screen.figma_node_id || '';
      return {
        ...base,
        id: screen.id || `${base.id}-screen-${index + 1}`,
        name: screen.name || `${base.name} ${index + 1}`,
        label: variant ? `Version ${variant}` : `화면 ${index + 1}`,
        figma_node_id: figmaNodeId,
        figmaNodeId,
        imageUrl,
        image_url: imageUrl,
        width: screen.width || 0,
        height: screen.height || 0,
        viewport: screen.viewport || '',
        order: Number.isFinite(Number(screen.order)) ? Number(screen.order) : index,
        assetId: screen.assetId || screen.asset_id || null,
        asset_id: screen.asset_id || screen.assetId || null,
      };
    });
  };

  const buildSourceData = async () => {
    if (type === 'ab') {
      const variants = {};
      for (const variant of ['A', 'B']) {
        const variantItems = getSourceItems(variant);
        if (form.source_type === 'image') {
          const imageItems = variantItems.filter((item) => item.kind === 'image' && item.file);
          const assets = await Promise.all(imageItems.map((item) => personaApi.uploadAsset(item.file, 'ab_test_source')));
          variants[variant] = imageItems.map((item, index) => ({
            id: `${variant.toLowerCase()}-screen-${index + 1}`,
            key: variant,
            label: `Version ${variant}`,
            name: item.name || `화면 ${index + 1}`,
            fileName: item.file?.name,
            fileSize: item.file?.size,
            assetId: assets[index]?.id,
            imageUrl: assets[index]?.url,
            image_url: assets[index]?.url,
          }));
        } else if (form.source_type === 'url') {
          const urlEntries = variantItems
            .filter((item) => item.kind === 'url')
            .map((item, index) => ({
              id: `${variant.toLowerCase()}-screen-${index + 1}`,
              key: variant,
              label: `Version ${variant}`,
              name: item.name || `화면 ${index + 1}`,
              url: item.url,
            }));
          variants[variant] = await Promise.all(urlEntries.map(captureUrlPreview));
        } else {
          variants[variant] = getSelectedFigmaScreenEntries(variant);
        }
      }
      return {
        source_type: form.source_type,
        scope_type: form.scope_type,
        variants,
        screens: ['A', 'B'].flatMap((variant) => variants[variant] || []),
      };
    }

    if (form.source_type === 'image' && (uploadFile || sourceItems.some((item) => item.kind === 'image' && !item.variant))) {
      const imageItems = sourceItems.filter((item) => item.kind === 'image' && !item.variant && item.file);
      const assets = await Promise.all(imageItems.map((item) => personaApi.uploadAsset(item.file, 'test_source')));
      const imageEntries = imageItems.map((item, index) => ({
        id: `screen-${index + 1}`,
        name: item.name || `화면 ${index + 1}`,
        fileName: item.file?.name,
        fileSize: item.file?.size,
        assetId: assets[index]?.id,
        imageUrl: assets[index]?.url,
      }));
      const firstEntry = imageEntries[0];
      return {
        asset_id: firstEntry?.assetId || null,
        image_url: firstEntry?.imageUrl || '',
        filename: firstEntry?.fileName || uploadFile?.name,
        imageEntries,
        image_entries: imageEntries,
        screens: imageEntries.map((entry) => ({
          id: entry.id,
          name: entry.name,
          filename: entry.fileName,
          size: entry.fileSize,
          image_url: entry.imageUrl,
          imageUrl: entry.imageUrl,
        })),
      };
    }
    if (form.source_type === 'figma') {
      const figmaScreen = buildFigmaSourceEntry();
      const figmaScreens = getSelectedFigmaScreenEntries();
      const { fileId, flowId } = getFigmaSelectionIds();
      return {
        figma_file_id: fileId || null,
        figma_file: selectedFigmaFile || null,
        figma_flow_id: flowId || null,
        figma_flow: selectedFigmaFlow || null,
        figmaPreview: selectedFigmaPreview || null,
        figma_preview: selectedFigmaPreview || null,
        figma_node_url: figmaScreen.figma_node_url,
        figmaNodeUrl: figmaScreen.figmaNodeUrl,
        figma_embed_url: figmaScreen.figma_embed_url,
        figmaEmbedUrl: figmaScreen.figmaEmbedUrl,
        figmaScreens,
        figma_screens: figmaScreens,
        screens: figmaScreens,
      };
    }
    const addedUrlItems = sourceItems.filter((item) => item.kind === 'url' && !item.variant);
    const typedUrl = normalizeHttpUrlInput(pendingUrl || form.source_url || '');
    const urlItems = addedUrlItems.length > 0 ? addedUrlItems : (typedUrl ? [{ name: '화면1', url: typedUrl }] : []);
    const urlEntries = urlItems
      .map((item, index) => ({
        id: `screen-${index + 1}`,
        name: item.name || `화면 ${index + 1}`,
        pageTitle: item.name || `화면 ${index + 1}`,
        url: item.url,
      }));
    const capturedUrlEntries = await Promise.all(urlEntries.map(captureUrlPreview));
    return {
      url: normalizeHttpUrlInput(form.source_url || getSourceItems()[0]?.url || ''),
      urls: capturedUrlEntries.map((item) => ({ name: item.name, url: item.url, capturedImageUrl: item.capturedImageUrl })),
      urlEntries: capturedUrlEntries,
      url_entries: capturedUrlEntries,
    };
  };

  const canStepOneContinue = Boolean(form.name.trim() && form.device_type);
  const sourceValidationError = getSourceValidationError();
  const canStepTwoContinue = !sourceValidationError;
  const canStepThreeContinue = selectedPersonaIds.length > 0;
  const ctaQuotaHintMessage = ctaQuotaMessage || (isQuotaExceeded ? quotaExceededMessage : '');
  const isCtaQuotaBlocked = Boolean(ctaQuotaMessage || isQuotaExceeded);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isCtaQuotaBlocked) {
      setCtaQuotaMessage(quotaExceededMessage);
      setError('');
      return;
    }
    if (!form.name.trim()) {
      setError('테스트 이름을 입력해 주세요.');
      setStep(1);
      return;
    }
    if (!canStepTwoContinue) {
      setError(sourceValidationError || '테스트 구성과 화면 제공 방식을 선택해 주세요.');
      setStep(2);
      return;
    }
    if (!canStepThreeContinue) {
      setError('테스트에 참여할 퍼소나를 1명 이상 선택해 주세요.');
      setStep(3);
      return;
    }
    if (selectedPersonaIds.length > personaSelectionLimit) {
      setError(`퍼소나는 최대 ${personaSelectionLimit}명까지 선택할 수 있습니다.`);
      setStep(3);
      return;
    }
    setSaving(true);
    setError('');
    setCtaQuotaMessage('');
    try {
      if (type === 'ab') {
        const sourceData = await buildSourceData();
        const sourceDataWithGoal = {
          ...sourceData,
          flow_goal: form.flow_goal,
        };
        const payload = {
          name: form.name,
          purpose: form.purpose || form.flow_goal || form.description,
          service_context: form.service_context,
          mode: form.scope_type,
          source_type: form.source_type,
          device_type: form.device_type,
          screens: sourceDataWithGoal.screens,
          context_data: {
            description: form.description,
            flow_goal: form.flow_goal,
            planning_brief: form.planning_brief,
            device_type: form.device_type,
            persona_ids: selectedPersonaIds.map(Number),
            personaSelection: {
              useAllPersonas: false,
              selectedPersonaIds,
            },
            source_data: sourceDataWithGoal,
          },
        };
        const created = await personaAbTestApi.createTest(payload);
        if (selectedPersonaIds.length > 0) {
          const runResponse = await personaAbTestApi.runTest(created.id, { persona_ids: selectedPersonaIds.map(Number) });
          if (runResponse?.success === false) {
            throw new Error(runResponse.error || 'A/B 비교검증 실행에 실패했습니다.');
          }
        }
        if (isMountedRef.current) navigate(`/tests/ab/${created.id}`);
        return;
      }

      const sourceData = await buildSourceData();
      const payload = {
        name: form.name,
        description: form.description,
        source_type: form.source_type,
        device_type: form.device_type,
        scope_type: form.scope_type,
        validation_type: form.validation_type,
        persona_count: selectedPersonaIds.length,
        source_data: {
          ...sourceData,
          flow_goal: form.flow_goal,
          planning_brief: form.planning_brief,
          personaSelection: {
            useAllPersonas: false,
            selectedPersonaIds,
          },
        },
      };
      const created = await personaTestApi.createTest(payload);
      if (selectedPersonaIds.length > 0) {
        const runResponse = await personaTestApi.runTest(created.id, { persona_ids: selectedPersonaIds.map(Number) });
        if (runResponse?.success === false) {
          throw new Error(runResponse.error || '테스트 실행에 실패했습니다.');
        }
      }
      if (isMountedRef.current) navigate(`/tests/ui/${created.id}`);
    } catch (err) {
      if (isQuotaExceededError(err)) {
        setCtaQuotaMessage(quotaExceededMessage);
        setError('');
      } else {
        setError(resolvePersonaApiErrorMessage(err, '테스트 생성에 실패했습니다.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const renderSourceList = (title, items = sourceItems) => {
    if (items.length === 0) return null;
    return (
      <div className="ux-source-list">
        <strong>{title}</strong>
        {items.map((item) => (
          <div key={item.id} className="ux-source-row">
            <span className="ux-source-row__thumb">
              <SourceThumbnail item={item} />
            </span>
            <div className="ux-source-row__copy">
              <input value={item.name} onChange={(event) => updateSourceItemName(item.id, event.target.value)} />
              <small>{item.kind === 'url' ? item.url : `${item.file?.name || '파일명'} · ${formatFileSize(item.file?.size || 0)}`}</small>
            </div>
            <button type="button" onClick={() => removeSourceItem(item.id)}>
              삭제
              <Trash2 size={18} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderImagePanel = () => (
    <div className="ux-source-panel">
      <div className="ux-source-note">
        <Info size={18} />
        <span>{sourceRequirementLabel('이미지')}</span>
      </div>
      <label
        className={`ux-upload-box${isSourceLimitReached('image') ? ' is-disabled' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
        }}
        onDrop={(event) => {
          event.preventDefault();
          if (!isSourceLimitReached('image')) {
            handleImageFiles(event.dataTransfer.files);
          }
        }}
      >
        <input
          type="file"
          accept="image/*"
          multiple
          disabled={isSourceLimitReached('image')}
          onChange={(event) => {
            handleImageFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <span>
          <Upload size={20} />
          첨부할 이미지를 끌어다 놓거나, 이미지 업로드 버튼을 선택해주세요.
        </span>
        <em>이미지 업로드</em>
      </label>
      {renderSourceList('추가한 이미지')}
    </div>
  );

  const renderVersionSourceList = (variant, kind) => {
    const items = getSourceItems(variant).filter((item) => item.kind === kind);
    return renderSourceList(`Version ${variant} ${kind === 'url' ? 'URL' : '이미지'}`, items);
  };

  const renderFigmaPrototypeSelect = (label = 'Figma 프로토타입 선택', variant = null) => {
    const selectKey = variant || 'default';
    const selectOpen = figmaSelectOpenKey === selectKey;
    const selectedFile = getSelectedFigmaFile(variant);
    const selectedFlow = getSelectedFigmaFlow(variant);
    const selectedOption = getSelectedFigmaPrototype(variant) || (selectedFile && selectedFlow ? { file: selectedFile, flow: selectedFlow } : null);
    const selectionIds = getFigmaSelectionIds(variant);
    return (
      <div className={`ux-figma-prototype-select${selectOpen ? ' is-open' : ''}`}>
        <button
          type="button"
          className="ux-figma-prototype-trigger"
          onClick={() => setFigmaSelectOpenKey((prev) => (prev === selectKey ? null : selectKey))}
          aria-label={label}
          aria-expanded={selectOpen}
        >
          {selectedOption ? (
            <>
              <div className="ux-figma-thumb">
                {selectedOption.file.thumbnail_url ? <img src={selectedOption.file.thumbnail_url} alt="" /> : <span>Figma<br />thumbnail</span>}
              </div>
              <div className="ux-figma-prototype-copy">
                <strong>
                  {selectedOption.file.figma_file_name || selectedOption.file.figma_file_key || '피그마 파일명'}
                  <em>{selectedOption.flow.figma_flow_name || 'Flow명'}</em>
                </strong>
                <p>{selectedOption.flow.figma_page_name || '피그마 파일 내 페이지 명'}</p>
              </div>
            </>
          ) : (
            <span className="ux-figma-prototype-placeholder">Figma 프로토타입을 선택해주세요.</span>
          )}
          <ChevronDown size={18} className="ux-figma-prototype-chevron" />
        </button>

        {selectOpen ? (
          <div className="ux-figma-prototype-menu" role="listbox" aria-label={label}>
            {figmaPrototypeOptions.length > 0 ? (
              figmaPrototypeOptions.map(({ file, flow }) => {
                const active = String(file.id) === String(selectionIds.fileId) && String(flow.id) === String(selectionIds.flowId);
                return (
                  <button
                    key={`${file.id}:${flow.id}`}
                    type="button"
                    className={`ux-figma-prototype-option${active ? ' is-selected' : ''}`}
                    onClick={() => {
                      setFigmaSelection(variant, file.id, flow.id);
                      setFigmaSelectOpenKey(null);
                    }}
                    role="option"
                    aria-selected={active}
                  >
                    <div className="ux-figma-thumb">
                      {file.thumbnail_url ? <img src={file.thumbnail_url} alt="" /> : <span>Figma<br />thumbnail</span>}
                    </div>
                    <div className="ux-figma-prototype-copy">
                      <strong>
                        {file.figma_file_name || file.figma_file_key}
                        <em>{flow.figma_flow_name || 'Flow명'}</em>
                      </strong>
                      <p>{flow.figma_page_name || '피그마 파일 내 페이지 명'}</p>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="ux-figma-prototype-empty">연결된 Flow가 없습니다.</div>
            )}
            <button
              type="button"
              className="ux-figma-prototype-add"
              onClick={() => {
                setFigmaSelectOpenKey(null);
                setFigmaModalTarget(variant);
                setFigmaModalError('');
                setFigmaModalOpen(true);
              }}
            >
              <Plus size={18} />
              피그마 파일 추가하기
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderFigmaPreview = (variant = null) => {
    const selectedFile = getSelectedFigmaFile(variant);
    const selectedFlow = getSelectedFigmaFlow(variant);
    const previewKey = getFigmaPreviewKey(variant);
    const previewScreens = getSelectedFigmaPreviewScreens(variant);
    const previewTransitions = getSelectedFigmaPreviewTransitions(variant);
    const previewLoading = previewKey ? Boolean(figmaPreviewLoadingByKey[previewKey]) : false;
    const previewError = previewKey ? figmaPreviewErrorsByKey[previewKey] : '';
    const screenCount = previewScreens.length || (selectedFlow ? 1 : 0);
    const transitionCount = previewTransitions.length;
    const showPreviewBadges = !previewLoading && (screenCount > 0 || transitionCount > 0);
    const hasOnePreviewScreen = previewScreens.length === 1 || (!previewScreens.length && Boolean(selectedFile?.thumbnail_url));
    return (
      <div className={`ux-figma-preview${previewLoading ? ' ux-figma-preview--loading' : ''}${hasOnePreviewScreen ? ' ux-figma-preview--one-screen' : ''}`}>
        {showPreviewBadges ? (
          <div className="ux-figma-preview__badges">
            <span>{screenCount} screens</span>
            <span>{transitionCount} transitions</span>
          </div>
        ) : null}
        {previewLoading ? (
          <FigmaPreviewLoading
            fileName={selectedFile?.figma_file_name || selectedFile?.figma_file_key}
            flowName={selectedFlow?.figma_flow_name}
          />
        ) : previewError ? (
          <p className="ux-figma-preview__message">{previewError}</p>
        ) : previewScreens.length > 0 ? (
          <div className="ux-figma-preview__grid">
            {previewScreens.map((screen, index) => (
              <figure key={screen.id || screen.figmaNodeId || index} className="ux-figma-preview-card">
                <div className="ux-figma-preview-card__scroll">
                  <FigmaPreviewImage
                    src={screen.imageUrl || screen.image_url}
                    alt={screen.name || `Figma 화면 ${index + 1}`}
                  />
                </div>
                <figcaption>
                  <b>{index + 1}</b>
                  <span>{screen.name || `Frame ${index + 1}`}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        ) : selectedFile?.thumbnail_url ? (
          <div className="ux-figma-preview__grid">
            <figure className="ux-figma-preview-card">
              <div className="ux-figma-preview-card__scroll">
                <img src={selectedFile.thumbnail_url} alt="" />
              </div>
              <figcaption>
                <b>1</b>
                <span>{selectedFlow?.figma_flow_name || selectedFile?.figma_file_name || 'Figma'}</span>
              </figcaption>
            </figure>
          </div>
        ) : (
          <p className="ux-figma-preview__message">선택된 프로토타입 이미지가 없습니다.</p>
        )}
      </div>
    );
  };

  const renderVersionImagePanel = (variant) => (
    <section className="ux-ab-version-panel">
      <h3>Version {variant}</h3>
      <div className="ux-source-panel">
        <div className="ux-source-note">
          <Info size={18} />
          <span>{sourceRequirementLabel('image')}</span>
        </div>
        <label
          className={`ux-upload-box${isSourceLimitReached('image', variant) ? ' is-disabled' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
          }}
          onDrop={(event) => {
            event.preventDefault();
            if (!isSourceLimitReached('image', variant)) {
              handleImageFiles(event.dataTransfer.files, variant);
            }
          }}
        >
          <input
            type="file"
            accept="image/*"
            multiple={!isAbSingleScreenImageSource('image')}
            disabled={isSourceLimitReached('image', variant)}
            onChange={(event) => {
              handleImageFiles(event.target.files, variant);
              event.target.value = '';
            }}
          />
          <span>첨부할 이미지를 끌어다 놓거나, 이미지 업로드 버튼을 선택해주세요.</span>
          <em>
            <Upload size={16} />
            이미지 업로드
          </em>
        </label>
        {renderVersionSourceList(variant, 'image')}
      </div>
    </section>
  );

  const renderVersionUrlPanel = (variant) => {
    const fieldName = `version_${variant.toLowerCase()}_url`;
    return (
      <section className="ux-ab-version-panel">
        <h3>Version {variant}</h3>
        <div className="ux-source-panel ux-source-panel--url ux-source-panel--ab-url">
          <div className="ux-source-note">
            <Info size={18} />
            <span>{sourceRequirementLabel('URL')}</span>
          </div>
          <div className="ux-url-add">
            <Input
              value={form[fieldName]}
              invalid={Boolean(urlErrors[variant])}
              onChange={(event) => {
                setField(fieldName, event.target.value);
                clearUrlError(variant);
              }}
              placeholder="URL주소를 입력해주세요"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddUrl(variant);
                }
              }}
            />
            <button type="button" onClick={() => handleAddUrl(variant)} disabled={!form[fieldName].trim() || isSourceLimitReached('url', variant)}>
              추가
            </button>
          </div>
          {urlErrors[variant] ? <p className="ux-url-error">{urlErrors[variant]}</p> : null}
          {renderVersionSourceList(variant, 'url')}
        </div>
      </section>
    );
  };

  const renderVersionFigmaPanel = (variant) => (
    <section className="ux-ab-version-panel">
      <div className="ux-source-panel ux-source-panel--figma ux-ab-figma-panel">
        <div className="ux-source-note">
          <Info size={18} />
          {form.scope_type === 'flow'
            ? <span>사용자 플로우는 선택한 프로토타입의 화면을 순서대로 최대 {AB_FLOW_SOURCE_ITEMS_LIMIT}개까지 분석합니다.</span>
            : <span>단일 화면은 선택한 프로토타입의 첫 번째 화면을 기준으로 진행됩니다.</span>
          }
        </div>
        <span className="ux-ab-figma-screen-tag">{variant} 화면</span>
        <div className="ux-ab-figma-divider" />
        <div className="ux-figma-picker">
          <div className="ux-figma-picker__label">
            <strong>Figma 프로토타입</strong>
            <button type="button" className="ux-icon-button" onClick={loadData} aria-label="Figma 프로토타입 새로고침">
              <RefreshCcw size={18} />
            </button>
          </div>
          <div className="ux-figma-connected">
            {renderFigmaPrototypeSelect(`Version ${variant} Figma 프로토타입 선택`, variant)}
            {renderFigmaPreview(variant)}
          </div>
        </div>
      </div>
    </section>
  );

  const renderAbSourcePanel = () => {
    if (!form.source_type) return null;
    if (form.source_type === 'figma') {
      if (!isFigmaConnected) {
        return renderFigmaPanel();
      }

      return (
        <div className="ux-ab-figma-connected">
          <div className="ux-figma-account">
            <div>
              <span className="ux-figma-account-avatar">
                {figmaAccountAvatar ? <img src={figmaAccountAvatar} alt="" /> : String(figmaAccountName || 'F').charAt(0).toUpperCase()}
              </span>
              <div>
                <b>{figmaAccountName}</b>
                <small>{figmaAccountEmail}</small>
              </div>
              {formatCompactDate((getSelectedFigmaFile('A') || getSelectedFigmaFile('B') || selectedFigmaFile)?.last_synced_at) && (
                <small className="ux-figma-updated">
                  최근업데이트: {formatCompactDate((getSelectedFigmaFile('A') || getSelectedFigmaFile('B') || selectedFigmaFile)?.last_synced_at)}
                </small>
              )}
              <button type="button" className="ux-icon-button" onClick={loadData} aria-label="새로고침">
                <RefreshCcw size={18} />
              </button>
              <button type="button" onClick={handleRequestFigmaChange}>변경</button>
            </div>
          </div>
          <div className="ux-ab-version-grid">
            {renderVersionFigmaPanel('A')}
            {renderVersionFigmaPanel('B')}
          </div>
        </div>
      );
    }

    return (
      <div className="ux-ab-version-grid">
        {form.source_type === 'image' ? renderVersionImagePanel('A') : renderVersionUrlPanel('A')}
        {form.source_type === 'image' ? renderVersionImagePanel('B') : renderVersionUrlPanel('B')}
      </div>
    );
  };

  const renderUrlPanel = () => (
    <div className="ux-source-panel ux-source-panel--url">
      <div className="ux-source-note">
        <Info size={18} />
        <span>{sourceRequirementLabel('URL')}</span>
      </div>
      <div className="ux-url-add">
        <Input
          value={pendingUrl}
          invalid={Boolean(urlErrors.default)}
          onChange={(event) => {
            setPendingUrl(event.target.value);
            clearUrlError('default');
          }}
          placeholder="URL주소를 입력해주세요"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleAddUrl();
            }
          }}
        />
        <button type="button" onClick={handleAddUrl} disabled={!pendingUrl.trim() || isSourceLimitReached('url')}>
          추가
        </button>
      </div>
      {urlErrors.default ? <p className="ux-url-error">{urlErrors.default}</p> : null}
      {renderSourceList('추가한 URL')}
    </div>
  );

  const renderFigmaPanel = () => {
    if (!isFigmaConnected) {
      return (
        <div className="ux-source-panel ux-source-panel--figma ux-figma-connect-panel">
          <div className="ux-figma-connect-card">
            <strong>Figma 연결필요</strong>
            <p>Figma 계정을 연동하면 프로토타입 혹은 화면을 불러올 수 있어요.</p>
            <button type="button" className="ux-mini-dark-button" onClick={handleConnectFigma}>
              <FigmaMark />
              Figma 계정연동
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="ux-source-panel ux-source-panel--figma">
        <div className="ux-source-note">
          <Info size={18} />
          {form.scope_type === 'flow'
            ? <span>사용자 플로우는 선택한 프로토타입의 화면을 순서대로 최대 {FLOW_SOURCE_ITEMS_LIMIT}개까지 분석합니다.</span>
            : <span>단일 화면은 선택한 프로토타입의 화면을 최대 {SINGLE_SOURCE_ITEMS_LIMIT}개까지 분석합니다.</span>
          }
        </div>
        <div className="ux-figma-account">
          <div>
            <span className="ux-figma-account-avatar">
              {figmaAccountAvatar ? <img src={figmaAccountAvatar} alt="" /> : String(figmaAccountName || 'F').charAt(0).toUpperCase()}
            </span>
            <div>
              <b>{figmaAccountName}</b>
              <small>{figmaAccountEmail}</small>
            </div>
            {formatCompactDate(selectedFigmaFile?.last_synced_at) && (
              <small className="ux-figma-updated">최근업데이트: {formatCompactDate(selectedFigmaFile?.last_synced_at)}</small>
            )}
            <button type="button" className="ux-icon-button" onClick={loadData} aria-label="새로고침">
              <RefreshCcw size={18} />
            </button>
            <button type="button" onClick={handleRequestFigmaChange}>변경</button>
          </div>
        </div>
        <div className="ux-figma-picker">
          <strong>Figma 프로토타입</strong>
          {figmaFiles.length > 0 ? (
            <div className="ux-figma-connected">
              {renderFigmaPrototypeSelect()}
              {renderFigmaPreview()}
            </div>
          ) : (
            <div className="ux-figma-prototype-empty-state">
              <div>
                <strong>Figma 프로토타입 선택</strong>
                <p>연결된 프로토타입이 없습니다. 추가하기로 Figma 파일을 연결해주세요.</p>
              </div>
              <button type="button" className="ux-mini-dark-button" onClick={() => {
                setFigmaModalError('');
                setFigmaModalOpen(true);
              }}>
                <Plus size={18} />
                추가하기
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStepOne = () => (
    <form className="ux-test-create-body" onSubmit={(event) => {
      event.preventDefault();
      if (canStepOneContinue) setStep(2);
    }}>
      <section className="ux-test-create-content">
        <header className="ux-test-section-title ux-test-section-title--with-action">
          <div>
            <h2>테스트 정보</h2>
            <p>확인하고자 하는 테스트의 이름과 내용을 입력해주세요.</p>
          </div>
          <button type="button" className="ux-mini-dark-button" onClick={openPlanModal}>
            <FileText size={18} />
            리서치 계획서 불러오기
          </button>
        </header>
        <label className="ux-create-field">
          <span>테스트 이름 <em>*</em></span>
          <input
            className="ux-create-test-name-input"
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            placeholder={createCopy.namePlaceholder}
            maxLength={60}
            autoFocus
          />
        </label>
        <label className="ux-create-field">
          <span>테스트 내용 (선택)</span>
          <textarea
            value={form.description}
            onChange={(event) => setField('description', event.target.value)}
            placeholder={createCopy.descriptionPlaceholder}
            rows={5}
          />
        </label>
        <div className="ux-create-field">
          <span>검증 디바이스</span>
          <div className="ux-create-choice-grid">
            <SelectCard
              active={form.device_type === 'pc'}
              title="PC"
              description="데스크톱 화면 기준으로 검증합니다."
              onClick={() => setField('device_type', 'pc')}
            />
            <SelectCard
              active={form.device_type === 'mobile'}
              title="Mobile"
              description="모바일 화면 기준으로 검증합니다."
              onClick={() => setField('device_type', 'mobile')}
            />
          </div>
        </div>
      </section>
      <footer className="ux-test-create-footer">
        <div>
          <button type="submit" className="ux-create-primary" disabled={!canStepOneContinue}>
            다음
            <ChevronRight size={24} />
          </button>
        </div>
      </footer>
      <ResearchPlanImportModal
        isOpen={planModalOpen}
        plans={planOptions}
        loading={planOptionsLoading}
        selectedPlanId={selectedPlanId}
        onSelect={setSelectedPlanId}
        onCancel={() => setPlanModalOpen(false)}
        onLoad={handleLoadPlanIntoTest}
      />
    </form>
  );

  const renderStepTwo = () => (
    <form className="ux-test-create-body" onSubmit={(event) => {
      event.preventDefault();
      if (canStepTwoContinue) setStep(3);
    }}>
      <section className="ux-test-create-content">
        <header className="ux-test-section-title">
          <h2>테스트 구성 선택</h2>
          <p>{type === 'ab' ? '테스트 범위와 그에 맞는 화면 제공 방식을 선택해주세요.' : '테스트 목표와 그에 맞는 화면 제공 방식을 선택해주세요.'}</p>
        </header>
        <div className="ux-create-field">
          <span>테스트 범위</span>
          <div className="ux-create-choice-grid">
            <SelectCard
              active={form.scope_type === 'screen'}
              title="단일 화면"
              description="개별 화면에 대한 UX 문제점을 알고 싶어요."
              onClick={() => handleScopeChange('screen')}
            />
            <SelectCard
              active={form.scope_type === 'flow'}
              title="사용자 플로우"
              description="전반적인 흐름 내에서 전환과 이탈의 요인을 알고 싶어요."
              onClick={() => handleScopeChange('flow')}
            />
          </div>
        </div>
        {form.scope_type === 'flow' ? (
          <label className="ux-create-field">
            <textarea
              value={form.flow_goal}
              onChange={(event) => setField('flow_goal', event.target.value)}
              placeholder="이 플로우에서 사용자가 달성해야 할 목표를 입력하세요. (선택)"
              rows={4}
            />
          </label>
        ) : null}
        {form.scope_type ? (
          <div className="ux-create-field">
            <span>화면 제공 방식</span>
            <div className="ux-source-options">
              {SOURCE_OPTIONS.map((option) => (
                <SelectCard
                  key={option.value}
                  active={form.source_type === option.value}
                  title={option.label}
                  icon={option.icon}
                  figma={option.figma}
                  compact
                  onClick={() => handleSourceChange(option.value)}
                />
              ))}
            </div>
            {type === 'ab' ? renderAbSourcePanel() : null}
            {type === 'ui' && form.source_type === 'image' ? renderImagePanel() : null}
            {type === 'ui' && form.source_type === 'url' ? renderUrlPanel() : null}
            {type === 'ui' && form.source_type === 'figma' ? renderFigmaPanel() : null}
          </div>
        ) : null}
      </section>
      <footer className="ux-test-create-footer">
        <div>
          <button type="button" className="ux-create-secondary" onClick={() => handlePreviousStep(1)}>
            <ChevronLeft size={24} />
            이전
          </button>
          <button type="submit" className="ux-create-primary" disabled={!canStepTwoContinue}>
            다음
            <ChevronRight size={24} />
          </button>
        </div>
      </footer>
    </form>
  );

  const renderPersonaCard = (persona) => {
    const profile = getPersonaProfile(persona);
    const ageLabel = profile.age ? `${profile.age}세` : '연령 미상';
    const metaLabel = [profile.gender, ageLabel, profile.job].filter(Boolean).join(' · ');
    const selected = selectedPersonaIds.includes(String(persona.id));
    const disabled = !selected && selectedPersonaIds.length >= personaSelectionLimit;
    return (
      <article
        key={persona.id}
        className={[
          'persona-list-card',
          'persona-list-card--editable',
          selected ? 'persona-list-card--selected is-selected' : '',
          disabled ? 'persona-list-card--disabled is-disabled' : '',
        ].filter(Boolean).join(' ')}
      >
        <span
          className="persona-check persona-list-card__check"
          role="checkbox"
          aria-checked={selected}
          tabIndex={disabled ? -1 : 0}
          onClick={() => {
            if (!disabled) togglePersona(persona.id);
          }}
          onKeyDown={(event) => {
            if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return;
            event.preventDefault();
            togglePersona(persona.id);
          }}
        >
          <input type="checkbox" checked={selected} disabled={disabled} readOnly tabIndex={-1} />
          <span />
        </span>
        <button
          type="button"
          className="persona-list-card__content"
          onClick={() => togglePersona(persona.id)}
          aria-pressed={selected}
          disabled={disabled}
        >
          <div className="persona-list-card__top">
            <PersonaAvatar persona={persona} size="sm" />
            <div className="persona-list-card__identity">
              <div className="persona-list-card__identity-main">
                <strong>{persona.name || '이름 없는 퍼소나'}</strong>
                <PersonaTagChip tag={persona.tag} />
              </div>
              <p className="persona-list-card__meta">{metaLabel}</p>
            </div>
          </div>
          <div className="persona-list-card__divider" />
          <section className="persona-list-card__section">
            <strong>성격</strong>
            <p>{profile.personality}</p>
          </section>
          <section className="persona-list-card__section">
            <strong>행동 패턴</strong>
            <p>{profile.behavior}</p>
          </section>
        </button>
      </article>
    );
  };

  const renderStepThree = () => {
    return (
      <form className="ux-test-create-body" onSubmit={(event) => {
        event.preventDefault();
        if (canStepThreeContinue) setStep(4);
      }}>
        <section className="ux-test-create-content ux-test-create-content--persona">
          <header className="ux-test-section-title">
            <h2>퍼소나 선택</h2>
            <p>테스트에 참여할 퍼소나를 선택해주세요. (최대 {personaSelectionLimit}명 선택 가능)</p>
          </header>
          {personas.length === 0 ? (
            <PersonaState title="선택할 Persona가 없습니다" message="먼저 persona를 생성해 주세요." />
          ) : (
            <div className="ux-persona-groups">
              {personaGroups.map((group) => {
                const selectedInGroup = group.items.filter((persona) => selectedPersonaIds.includes(String(persona.id))).length;
                const collapsed = collapsedPersonaGroupIds.map(String).includes(String(group.id));
                const gridId = `ux-persona-group-${group.id}`;
                return (
                  <section key={group.id} className={`ux-persona-group${collapsed ? ' is-collapsed' : ''}`}>
                    <header onClick={() => togglePersonaGroup(group.id)}>
                      <div>
                        <Folder size={30} fill="#1f2023" />
                        <div>
                          <strong>{group.name}</strong>
                          <small>총 퍼소나 {group.count}</small>
                        </div>
                      </div>
                      <span>{selectedInGroup}명 선택</span>
                      <button
                        type="button"
                        className="ux-persona-group-toggle"
                        onClick={(event) => {
                          event.stopPropagation();
                          togglePersonaGroup(group.id);
                        }}
                        aria-expanded={!collapsed}
                        aria-controls={gridId}
                        aria-label={`${group.name} 퍼소나 ${collapsed ? '펼치기' : '접기'}`}
                      >
                        <ChevronDown size={24} />
                      </button>
                    </header>
                    {!collapsed ? (
                      <div className="ux-persona-grid" id={gridId}>
                        {group.items.map(renderPersonaCard)}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </div>
          )}
        </section>
        <footer className="ux-test-create-footer">
          <div>
            <button type="button" className="ux-create-secondary" onClick={() => handlePreviousStep(2)}>
              <ChevronLeft size={24} />
              이전
            </button>
            <button type="submit" className="ux-create-primary" disabled={!canStepThreeContinue}>
              다음
              <ChevronRight size={24} />
            </button>
          </div>
        </footer>
      </form>
    );
  };

  const renderReviewRow = (label, value) => (
    <div className="ux-review-row">
      <span>{label}</span>
      <strong>{value || '-'}</strong>
    </div>
  );

  const renderReviewSection = (stepLabel, title, children) => (
    <section className="ux-review-section">
      <header>
        <span>{stepLabel}</span>
        <strong>{title}</strong>
      </header>
      <div>{children}</div>
    </section>
  );

  const getVariantSourceSummary = (variant) => {
    if (form.source_type === 'figma') {
      const figmaFile = getSelectedFigmaFile(variant);
      const figmaFlow = getSelectedFigmaFlow(variant);
      return figmaFlow?.figma_flow_name || figmaFile?.figma_file_name || 'Figma 프로토타입';
    }
    const items = getSourceItems(variant).filter((item) => item.kind === form.source_type);
    if (form.source_type === 'url') return `${items.length}개 URL`;
    return `${items.length}개 이미지`;
  };

  const renderStepFour = () => (
    <form className="ux-test-create-body" onSubmit={handleSubmit}>
      <section className="ux-test-create-content ux-test-create-content--review">
        <header className="ux-test-section-title">
          <h2>테스트 정보 확인</h2>
          <p>입력한 테스트 정보를 확인하고 생성을 시작해주세요.</p>
        </header>
        <div className="ux-review-list">
          {renderReviewSection('Step.01', '기본 정보', (
            <>
              {renderReviewRow('테스트 유형', createCopy.typeLabel)}
              {renderReviewRow('테스트 이름', form.name)}
              {renderReviewRow('테스트 내용', form.description.trim() || '-')}
              {renderReviewRow('검증 디바이스', selectedDeviceLabel)}
            </>
          ))}
          {renderReviewSection('Step.02', '테스트 구성 선택', (
            <>
              {renderReviewRow('테스트 범위', selectedScopeLabel)}
              {form.scope_type === 'flow' ? renderReviewRow('달성 목표', form.flow_goal || '입력 안함') : null}
              {renderReviewRow('화면 제공 방식', selectedSourceLabel)}
              {type === 'ab' && form.source_type !== 'image' ? renderReviewRow('Version A', getVariantSourceSummary('A')) : null}
              {type === 'ab' && form.source_type !== 'image' ? renderReviewRow('Version B', getVariantSourceSummary('B')) : null}
              {type === 'ui' && form.source_type === 'url' ? renderReviewRow('추가한 URL', getSourceItems()[0]?.url || form.source_url) : null}
              {type === 'ui' && form.source_type === 'figma' ? renderReviewRow('Figma 파일', selectedFigmaFile?.figma_file_name || selectedFigmaFile?.figma_file_key) : null}
            </>
          ))}
          {renderReviewSection('Step.03', '퍼소나 선택', (
            <>
              {renderReviewRow('퍼소나 수', `${selectedPersonaIds.length}명`)}
            </>
          ))}
        </div>
      </section>
      <footer className="ux-test-create-footer">
        <div>
          <TokenQuotaCtaHint
            remainingTokens={remainingTokens}
            loading={quotaLoading}
            warningMessage={isCtaQuotaBlocked ? ctaQuotaHintMessage : ''}
          />
          <button type="button" className="ux-create-secondary" onClick={() => handlePreviousStep(3)}>
            <ChevronLeft size={24} />
            이전
          </button>
          <button type="submit" className="ux-create-primary" disabled={saving || isCtaQuotaBlocked}>
            생성하기
          </button>
        </div>
      </footer>
    </form>
  );

  const renderAnalysisLoading = () => (
    <B2BLayout hideCollapseButton={false} defaultCollapsed={true} hidePageHeader={true}>
      <main className="persona-generation-progress">
        <TestGenerationProgress stages={getTestAnalysisSteps(type, form.scope_type)} />
      </main>
    </B2BLayout>
  );

  if (loading) {
    return (
      <UxCreateShell
        step={step}
        error={error}
        onBack={handleBackToList}
        title={createCopy.title}
        description={createCopy.description}
        navigationGuard={handleNavigationGuard}
      >
        <PersonaState type="loading" title="불러오는 중" message="Persona와 Figma 연결 정보를 가져오고 있습니다." />
      </UxCreateShell>
    );
  }

  if (saving) {
    return renderAnalysisLoading();
  }

  return (
    <UxCreateShell
      step={step}
      error={error}
      onBack={handleBackToList}
      title={createCopy.title}
      description={createCopy.description}
      navigationGuard={handleNavigationGuard}
    >
      {step === 1 ? renderStepOne() : null}
      {step === 2 ? renderStepTwo() : null}
      {step === 3 ? renderStepThree() : null}
      {step === 4 ? renderStepFour() : null}
      {planBriefLoading ? (
        <div className="ai-interview-loading" role="status" aria-live="polite">
          <span className="ai-interview-spinner" />
          <p>계획서를 불러오고 있습니다.</p>
        </div>
      ) : null}
      {figmaModalOpen ? (
        <div
          className="ux-modal-backdrop"
          role="presentation"
        >
          <form
            className="ux-figma-modal"
            onSubmit={handleSyncFigmaFile}
            onClick={(event) => event.stopPropagation()}
          >
            <header>
              <h2>Figma 파일 연결</h2>
            </header>
            <div className="ux-figma-modal__body">
              <ul>
                <li>최소 1개 이상의 Flow(프로토타입)가 설정되어 있어야 합니다.</li>
                <li>본인이 소유자(Owner) 권한을 가진 파일만 연결 가능합니다.</li>
                <li>파일 공유 설정은 Anyone 및 can view로 설정되어야 합니다.</li>
              </ul>
              <label className={figmaModalError ? 'is-error' : undefined}>
                <span>Figma URL</span>
                <input
                  value={figmaUrlInput}
                  onChange={(event) => {
                    setFigmaUrlInput(event.target.value);
                    setFigmaModalError('');
                  }}
                  placeholder="URL 주소를 입력해주세요"
                  autoFocus
                />
                {figmaModalError ? <p className="ux-figma-modal-error">{figmaModalError}</p> : null}
              </label>
            </div>
            <footer>
              <button
                type="button"
                onClick={() => {
                  setFigmaModalOpen(false);
                  setFigmaModalTarget(null);
                  setFigmaUrlInput('');
                  setFigmaModalError('');
                }}
                disabled={syncingFigma}
              >
                취소하기
              </button>
              <button type="submit" disabled={!figmaUrlInput.trim() || syncingFigma}>
                연결하기
              </button>
            </footer>
          </form>
        </div>
      ) : null}
      {syncingFigma ? (
        <div className="ux-figma-loading">
          <div className="ux-figma-loading__card" role="status" aria-live="polite">
            <Loader2 size={32} />
            <strong>파일 연결 중입니다</strong>
            <p>Figma 파일과 프로토타입 정보를 불러오고 있어요.</p>
          </div>
        </div>
      ) : null}
      <PageLeaveConfirmModal
        isOpen={Boolean(pendingExit)}
        message="작성 중인 테스트 정보가 저장되지 않습니다."
        confirmText={pendingExit?.type === 'step' ? '이전으로 이동' : '나가기'}
        onCancel={handleCancelPendingExit}
        onConfirm={handleConfirmPendingExit}
      />
      <ConfirmModal
        isOpen={figmaChangeConfirmOpen}
        type="warning"
        size="sm"
        align="dialog"
        className="ux-figma-change-confirm-modal"
        title="Figma 계정을 변경하시겠습니까?"
        message={`원활한 연동을 위해 기존 계정 연결을 종료해야 합니다.\n기존 계정 연결을 해제하고 새로운 피그마 계정으로\n로그인하시겠습니까?`}
        showCancel
        cancelText="취소"
        confirmText={figmaChanging ? '확인 중' : '확인'}
        onCancel={() => {
          if (!figmaChanging) setFigmaChangeConfirmOpen(false);
        }}
        onConfirm={handleConfirmFigmaChange}
      />
    </UxCreateShell>
  );
}

export default TestCreatePage;
